import { useState, useCallback } from "react";
import { DirectUpload } from "@rails/activestorage";
import type { DirectUploadDelegate, BlobUploadResponse } from "../types/activestorage.d";

interface UseDirectUploadReturn {
  uploadFile: (file: File) => Promise<string>;
  uploading: boolean;
  progress: number;
  error: string | null;
}

export function useDirectUpload(): UseDirectUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(async (file: File): Promise<string> => {
    setUploading(true);
    setProgress(0);
    setError(null);

    return new Promise((resolve, reject) => {
      const url = "/rails/active_storage/direct_uploads";
      
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
          xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              setProgress(Math.round(percentComplete));
            }
          });
        },
      });

      upload.create((error: Error | null, blob: BlobUploadResponse) => {
        setUploading(false);
        
        if (error) {
          const errorMessage = error.message || "Upload failed";
          setError(errorMessage);
          reject(new Error(errorMessage));
        } else {
          setProgress(100);
          resolve(blob.signed_id);
        }
      });
    });
  }, []);

  return {
    uploadFile,
    uploading,
    progress,
    error,
  };
}
